import PaymentTypeForm from "./components/NewPaymentTypeForm";
import { Button, IconButton, TableCell } from "@material-ui/core";
import { Delete, Edit, Warning } from "@material-ui/icons";
import TableView from "@shared/components/Table/TableView";
import { ITableHeaderCell } from "@shared/components/Table/types/ITableHeaderCell";
import { useConfirmationDialog } from "@shared/context/ConfirmationDialogContext";
import { IPaymentTypeDto } from "@shared/services/settings/dtos/payment-types/paymentTypeDto";
import {
  getAllPaymentTypes,
  removePaymentType,
} from "@shared/services/settings/payment-types.service";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { FC, useCallback, useEffect, useState } from "react";

export interface IPaymentType {
  id: number;
  name: string;
  createdOn?: string;
  deletedOn?: string;
  createdByName?: string;
  createdByUsername?: string;
  employeeCount?: number | string;
  onEditClick?: (id: number) => void;
  onDeleteClick?: (id: number) => void;
}

const createPaymentType = (
  id: number,
  name: string,
  createdOn?: string,
  onEditClick?: (id: number) => void,
  onDeleteClick?: (id: number) => void
): IPaymentType => ({
  id,
  name,
  createdOn,
  onEditClick,
  onDeleteClick,
});

const headerCells: ITableHeaderCell<IPaymentType>[] = [
  { id: "id", label: "ID" },
  { id: "name", label: "Име" },
  {
    id: "createdOn",
    label: "Датум и време на додавање",
  },
  { id: "editButton", label: "", disableSorting: true },
  { id: "deleteButton", label: "", disableSorting: true },
];

const createRow = (row: IPaymentType) => (
  <>
    <TableCell>{row.id}</TableCell>
    <TableCell>{row.name}</TableCell>
    <TableCell>{dayjs.utc(row.createdOn).local().format("LLL")}</TableCell>
    <TableCell padding="checkbox">
      <IconButton size="small" onClick={() => (row.onEditClick ? row.onEditClick(row.id) : null)}>
        <Edit />
      </IconButton>
    </TableCell>
    <TableCell padding="checkbox">
      <IconButton
        size="small"
        onClick={() => (row.onDeleteClick ? row.onDeleteClick(row.id) : null)}
      >
        <Delete />
      </IconButton>
    </TableCell>
  </>
);

const PaymentTypeViewPage: FC = () => {
  const {
    data: paymentTypes,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery(["paymentTypes"], getAllPaymentTypes, {
    refetchInterval: 15000,
  });

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  useEffect(() => {
    if (!error || !(error instanceof Error)) return;

    enqueueSnackbar(`Се случи грешка: ${error.message}`, {
      variant: "error",
      persist: true,
      preventDuplicate: true,
      anchorOrigin: { horizontal: "center", vertical: "bottom" },
      action: function onDismiss(key) {
        return <Button onClick={() => closeSnackbar(key)}>OK</Button>;
      },
    });
  }, [error, enqueueSnackbar, closeSnackbar]);

  const confirmationDialog = useConfirmationDialog();

  const handleDeleteClick = (id: number) => {
    const paymentType = paymentTypes.find((x: IPaymentTypeDto) => x.id === id);

    if (paymentType) {
      confirmationDialog.open({
        icon: <Warning fontSize="large" style={{ color: "orange" }} />,
        title: "Избриши тип",
        body: `Дали сте сигурни дека сакате да го избришете типот ${paymentType.name} со ID ${paymentType.id}?`,
        onConfirm: () => handleDelete(paymentType.id),
        onDeny: () => confirmationDialog.close(),
      });
    }
  };

  const handleDelete = useCallback(
    (id: number) => {
      confirmationDialog.setLoading(true);

      removePaymentType(id)
        .then(() => {
          confirmationDialog.close();
          enqueueSnackbar("Успешно го избришавте типот на наплата", {
            variant: "success",
            autoHideDuration: 6000,
          });

          refetch();
        })
        .catch((error: AxiosError) => {
          confirmationDialog.close();
          enqueueSnackbar(`Не успеа да се отстрани типот на наплата: ${error.message}`, {
            variant: "error",
            persist: true,
            preventDuplicate: true,
            anchorOrigin: { horizontal: "center", vertical: "bottom" },
            action: function onDismiss(key) {
              return <Button onClick={() => closeSnackbar(key)}>OK</Button>;
            },
          });
        });
    },
    [closeSnackbar, enqueueSnackbar, refetch]
  );

  const handleEditClick = (id: number) => {
    const paymentType = paymentTypes.find((x: IPaymentTypeDto) => x.id === id);

    if (paymentType) setSelectedRow(paymentType);
  };

  const [selectedRow, setSelectedRow] = useState<IPaymentType | null>(null);
  const handleAddTypeClick = () => {
    setSelectedRow(createPaymentType(0, ""));
  };

  const handleAddTypeDialogClose = () => {
    setSelectedRow(null);
  };

  // Map data
  const rows: IPaymentType[] = [];
  if (paymentTypes) {
    paymentTypes.forEach((x: IPaymentType) => {
      rows.push(createPaymentType(x.id, x.name, x.createdOn, handleEditClick, handleDeleteClick));
    });
  }

  return (
    <TableView
      title="Типови на наплата"
      addButtonText="Додади тип"
      onAddButtonClick={handleAddTypeClick}
      onRefreshClick={() => refetch()}
      headers={headerCells}
      rows={rows}
      render={createRow}
      isLoading={isLoading || isFetching}
      addDialog={
        <PaymentTypeForm
          title={selectedRow?.id ? "Промени тип на наплата" : "Додади тип на наплата"}
          addButtonText={selectedRow?.id ? "Зачувај" : "Додади"}
          open={selectedRow ? true : false}
          row={selectedRow?.id ? selectedRow : null}
          onClose={handleAddTypeDialogClose}
          onSuccess={() => refetch()}
        />
      }
    />
  );
};

export default PaymentTypeViewPage;
